import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { 
  Headphones, Search, Filter, Clock, CheckCircle2, AlertCircle, XCircle,
  MessageSquare, Send, RefreshCw, ArrowLeft, User, BookOpen, Eye
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

const STATUSES = ["open", "in_progress", "resolved", "closed"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const CATEGORIES = ["General Inquiry", "Billing", "Claims", "Policy", "Technical Issue", "Account"];

export default function AgentSupportTickets() {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  
  // Selected ticket
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Reply state
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);
  
  // Status update
  const [newStatus, setNewStatus] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  
  // Convert to KB dialog
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [kbTitle, setKbTitle] = useState("");
  const [kbCategory, setKbCategory] = useState("General");
  const [kbTags, setKbTags] = useState("");
  const [converting, setConverting] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem("agent_token");
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (priorityFilter) params.append("priority", priorityFilter);
      if (categoryFilter) params.append("category", categoryFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/agent/tickets?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, categoryFilter, search]);

  const fetchStats = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/tickets/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets]);

  const fetchTicketDetails = async (ticketId) => {
    setLoadingDetails(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketDetails(response.data);
      setSelectedTicket(ticketId);
      setNewStatus(response.data.ticket?.status || "");
    } catch (error) {
      toast.error("Failed to load ticket details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    
    setSending(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/tickets/${selectedTicket}/reply`, 
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reply sent");
      setReplyMessage("");
      fetchTicketDetails(selectedTicket);
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    
    setUpdating(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/agent/tickets/${selectedTicket}/status`, 
        { status: newStatus, internal_notes: internalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Status updated to ${newStatus}`);
      setInternalNotes("");
      fetchTicketDetails(selectedTicket);
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleConvertToKB = async () => {
    if (!kbTitle) {
      toast.error("Please enter a title");
      return;
    }
    
    setConverting(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/knowledge-base/from-ticket`, {
        ticket_id: selectedTicket,
        title: kbTitle,
        category: kbCategory,
        tags: kbTags.split(",").map(t => t.trim()).filter(Boolean)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("KB article created from ticket");
      setConvertDialogOpen(false);
      setKbTitle("");
      setKbCategory("General");
      setKbTags("");
      fetchTicketDetails(selectedTicket);
    } catch (error) {
      toast.error("Failed to create KB article");
    } finally {
      setConverting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: "bg-yellow-100 text-yellow-800",
      in_progress: "bg-blue-100 text-blue-800",
      resolved: "bg-green-100 text-green-800",
      closed: "bg-gray-100 text-gray-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: "bg-gray-100 text-gray-800",
      medium: "bg-blue-100 text-blue-800",
      high: "bg-orange-100 text-orange-800",
      urgent: "bg-red-100 text-red-800"
    };
    return styles[priority] || "bg-gray-100 text-gray-800";
  };

  return (
    <AgentLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Young Serif, serif' }}>
          <Headphones className="h-8 w-8 text-primary" />
          Support Tickets
        </h1>
        <p className="text-muted-foreground mt-1">Manage customer support requests</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <Card className="card-organic cursor-pointer hover:shadow-md" onClick={() => setStatusFilter("")}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md bg-yellow-50" onClick={() => setStatusFilter("open")}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-yellow-800">{stats.open}</p>
              <p className="text-xs text-yellow-600">Open</p>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md bg-blue-50" onClick={() => setStatusFilter("in_progress")}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-blue-800">{stats.in_progress}</p>
              <p className="text-xs text-blue-600">In Progress</p>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md bg-green-50" onClick={() => setStatusFilter("resolved")}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-green-800">{stats.resolved}</p>
              <p className="text-xs text-green-600">Resolved</p>
            </CardContent>
          </Card>
          <Card className="card-organic cursor-pointer hover:shadow-md bg-gray-50" onClick={() => setStatusFilter("closed")}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-gray-800">{stats.closed}</p>
              <p className="text-xs text-gray-600">Closed</p>
            </CardContent>
          </Card>
          <Card className="card-organic bg-red-50">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-red-800">{stats.urgent_pending}</p>
              <p className="text-xs text-red-600">Urgent</p>
            </CardContent>
          </Card>
          <Card className="card-organic bg-orange-50">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-orange-800">{stats.high_pending}</p>
              <p className="text-xs text-orange-600">High Priority</p>
            </CardContent>
          </Card>
        </div>
      )}

      {selectedTicket && ticketDetails ? (
        /* Ticket Detail View */
        <div>
          <Button variant="ghost" onClick={() => { setSelectedTicket(null); setTicketDetails(null); }} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tickets
          </Button>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Card className="card-organic">
                <CardHeader className="border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                        {ticketDetails.ticket?.subject}
                      </CardTitle>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge className={getStatusBadge(ticketDetails.ticket?.status)}>
                          {ticketDetails.ticket?.status?.replace('_', ' ')}
                        </Badge>
                        <Badge className={getPriorityBadge(ticketDetails.ticket?.priority)}>
                          {ticketDetails.ticket?.priority}
                        </Badge>
                        <Badge variant="outline">{ticketDetails.ticket?.category}</Badge>
                        <code className="text-xs bg-muted px-2 py-1 rounded">{ticketDetails.ticket?.ticket_id}</code>
                      </div>
                    </div>
                    {ticketDetails.ticket?.status !== 'closed' && !ticketDetails.ticket?.converted_to_kb && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setKbTitle(ticketDetails.ticket?.subject);
                          setConvertDialogOpen(true);
                        }}
                        data-testid="convert-to-kb-btn"
                      >
                        <BookOpen className="h-4 w-4 mr-1" />
                        Create KB Article
                      </Button>
                    )}
                    {ticketDetails.ticket?.converted_to_kb && (
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <BookOpen className="h-3 w-3" />
                        KB: {ticketDetails.ticket.converted_to_kb}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Original Description */}
                  <div className="bg-muted p-4 rounded-lg mb-6">
                    <p className="text-sm text-muted-foreground mb-2">Original Message:</p>
                    <p className="whitespace-pre-wrap">{ticketDetails.ticket?.description}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(ticketDetails.ticket?.created_at).toLocaleString()}
                    </p>
                  </div>
                  
                  {/* Replies */}
                  <div className="space-y-4 mb-6">
                    {ticketDetails.replies?.map((reply) => (
                      <div 
                        key={reply.reply_id}
                        className={`p-4 rounded-lg ${
                          reply.sender_type === 'agent' 
                            ? 'bg-primary/5 border-l-4 border-primary ml-4' 
                            : 'bg-muted mr-4'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-sm">
                            {reply.sender_type === 'agent' ? (
                              <span className="text-primary">{reply.sender_name} (Agent)</span>
                            ) : (
                              <span>{reply.sender_name} (Customer)</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(reply.created_at).toLocaleString()}
                          </p>
                        </div>
                        <p className="whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Reply Form */}
                  {ticketDetails.ticket?.status !== 'closed' && (
                    <div className="border-t pt-6">
                      <Label className="mb-2 block">Reply to Customer</Label>
                      <Textarea
                        placeholder="Type your response..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        rows={4}
                        className="mb-4"
                        data-testid="agent-reply-textarea"
                      />
                      <Button 
                        onClick={handleSendReply}
                        disabled={sending || !replyMessage.trim()}
                        data-testid="send-reply-btn"
                      >
                        {sending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        Send Reply
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              {/* Customer Info */}
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Customer
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="font-medium">{ticketDetails.user?.full_name || "N/A"}</p>
                  <p className="text-sm text-muted-foreground">{ticketDetails.user?.email}</p>
                  {ticketDetails.user?.phone && (
                    <p className="text-sm text-muted-foreground">{ticketDetails.user.phone}</p>
                  )}
                  {ticketDetails.user?.account_number && (
                    <p className="text-xs font-mono text-primary">{ticketDetails.user.account_number}</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Status Update */}
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="text-sm">Update Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => (
                        <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Internal notes (optional)"
                    value={internalNotes}
                    onChange={(e) => setInternalNotes(e.target.value)}
                    rows={2}
                  />
                  <Button 
                    onClick={handleUpdateStatus}
                    disabled={updating || newStatus === ticketDetails.ticket?.status}
                    className="w-full"
                    data-testid="update-status-btn"
                  >
                    {updating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Update Status
                  </Button>
                </CardContent>
              </Card>
              
              {/* Ticket Info */}
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="text-sm">Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Created</span>
                    <span>{new Date(ticketDetails.ticket?.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Updated</span>
                    <span>{new Date(ticketDetails.ticket?.updated_at).toLocaleDateString()}</span>
                  </div>
                  {ticketDetails.ticket?.resolved_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Resolved</span>
                      <span>{new Date(ticketDetails.ticket.resolved_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {ticketDetails.ticket?.internal_notes && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-muted-foreground mb-1">Internal Notes:</p>
                      <p className="text-xs bg-muted p-2 rounded">{ticketDetails.ticket.internal_notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        /* Ticket List View */
        <>
          {/* Filters */}
          <Card className="card-organic mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by ID, subject, email..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                      data-testid="ticket-search"
                    />
                  </div>
                </div>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={priorityFilter || "all"} onValueChange={(v) => setPriorityFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => { setStatusFilter(""); setPriorityFilter(""); setCategoryFilter(""); setSearch(""); }}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tickets Table */}
          <Card className="card-organic">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : tickets.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>Subject</th>
                        <th>Customer</th>
                        <th>Category</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => (
                        <tr key={ticket.ticket_id} className="cursor-pointer hover:bg-muted/50" onClick={() => fetchTicketDetails(ticket.ticket_id)}>
                          <td><code className="text-xs">{ticket.ticket_id}</code></td>
                          <td className="max-w-[200px]">
                            <p className="truncate font-medium">{ticket.subject}</p>
                          </td>
                          <td>
                            <p className="text-sm">{ticket.user_name || ticket.user_email}</p>
                          </td>
                          <td><Badge variant="outline">{ticket.category}</Badge></td>
                          <td><Badge className={getPriorityBadge(ticket.priority)}>{ticket.priority}</Badge></td>
                          <td><Badge className={getStatusBadge(ticket.status)}>{ticket.status?.replace('_', ' ')}</Badge></td>
                          <td className="text-sm text-muted-foreground">{new Date(ticket.created_at).toLocaleDateString()}</td>
                          <td>
                            <Button size="sm" variant="outline" data-testid={`view-ticket-${ticket.ticket_id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Headphones className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tickets found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Convert to KB Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
              Create Knowledge Base Article
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Create a KB article from this ticket's solution. The content will be generated from agent replies.
            </p>
            <div>
              <Label>Article Title *</Label>
              <Input
                value={kbTitle}
                onChange={(e) => setKbTitle(e.target.value)}
                placeholder="How to..."
                data-testid="kb-title-input"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={kbCategory} onValueChange={setKbCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={kbTags}
                onChange={(e) => setKbTags(e.target.value)}
                placeholder="billing, payment, refund"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConvertToKB} disabled={converting || !kbTitle} data-testid="create-kb-btn">
              {converting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <BookOpen className="h-4 w-4 mr-2" />}
              Create Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
